import { NATIVE_SMART_CONTRACT } from "../helpers/evmV2Filters.js"
import { EvmV2Network, getEvmV2Network } from "./evmV2Networks.js"
import { CurrencyActivityRow, toCurrencyActivity } from "../helpers/currencyActivity.js"

// Address statistics on Bitquery GraphQL v2 (v1 no longer serves EVM chains).
// Counts cover successful transfers of every kind (token, native, internal) —
// the same set the tracer follows. `toAddressInfo` reshapes the answer into
// the v1 layout the rest of the app reads.
const addressInfoQuery = (v2Network: string) => `query ($address: String!) {
    EVM(network: ${v2Network}, dataset: combined) {
      balance: Balances(
        where: {Balance: {Address: {is: $address}}, Currency: {SmartContract: {is: "${NATIVE_SMART_CONTRACT}"}}}
      ) {
        Balance {
          Amount
          AmountInUSD
        }
      }
      ins: Transfers(where: {Transfer: {Receiver: {is: $address}, Success: true}}) {
        count
        Block {
          min_date: Time(minimum: Block_Time)
          max_date: Time(maximum: Block_Time)
        }
      }
      currencyIn: Transfers(
        where: {Transfer: {Receiver: {is: $address}, Success: true}}
        orderBy: {descendingByField: "count"}
        limit: {count: 200}
      ) {
        count
        usd: sum(of: Transfer_AmountInUSD)
        Block {
          first_date: Time(minimum: Block_Time)
          last_date: Time(maximum: Block_Time)
        }
        Transfer {
          Currency {
            Symbol
            SmartContract
          }
        }
      }
      outs: Transfers(where: {Transfer: {Sender: {is: $address}, Success: true}}) {
        count
        Block {
          min_date: Time(minimum: Block_Time)
          max_date: Time(maximum: Block_Time)
        }
      }
      currencyOut: Transfers(
        where: {Transfer: {Sender: {is: $address}, Success: true}}
        orderBy: {descendingByField: "count"}
        limit: {count: 200}
      ) {
        count
        usd: sum(of: Transfer_AmountInUSD)
        Block {
          first_date: Time(minimum: Block_Time)
          last_date: Time(maximum: Block_Time)
        }
        Transfer {
          Currency {
            Symbol
            SmartContract
          }
        }
      }
    }
  }`

interface V2Counts {
    count: string | number
    Block: { min_date: string | null, max_date: string | null }
}

interface V2CurrencyCount extends CurrencyActivityRow {
    count: string | number
    Transfer: { Currency: { Symbol: string, SmartContract: string } }
}

export interface EvmAddressInfoV2Response {
    EVM: {
        balance: Array<{ Balance: { Amount: string, AmountInUSD: string } }>
        ins: V2Counts[]
        outs: V2Counts[]
        currencyIn: V2CurrencyCount[]
        currencyOut: V2CurrencyCount[]
    }
}

const toCounts = (rows: V2Counts[]) => ({
    count: Number(rows?.[0]?.count ?? 0),
    min_date: rows?.[0]?.Block?.min_date ?? null,
    max_date: rows?.[0]?.Block?.max_date ?? null,
})

// v1 listed the native coin with address "-"; flows keep using that
// convention (the UI then filters by its symbol, which the tracer maps back
// to native).
const toCurrencies = (rows: V2CurrencyCount[], network: EvmV2Network) => (rows ?? []).map((row) => ({
    count: Number(row.count),
    ...toCurrencyActivity(row),
    // A pegged native coin (and its wrapped twin) is priced even where v2 has no USD
    ...(network.nativeUsdPeg && isNativeOrWrapped(network, row.Transfer.Currency.SmartContract) ?
        { priced: true } :
        {}),
    currency: {
        symbol: row.Transfer.Currency.Symbol,
        address: row.Transfer.Currency.SmartContract === NATIVE_SMART_CONTRACT ?
            "-" :
            row.Transfer.Currency.SmartContract,
        tokenId: "",
    },
}))

const isNativeOrWrapped = (network: EvmV2Network, smartContract: string) =>
    smartContract === NATIVE_SMART_CONTRACT || smartContract.toLowerCase() === network.wrappedNative.toLowerCase()

const createEvmV2Blockchain = (network: EvmV2Network) => ({
    name: network.network,
    title: network.title,
    currency: network.nativeSymbol,
    feature: network.feature,
    check: true,
    regexp: '([\\W]|^)(0x[0-9a-fA-F]{40})([\\W]|$)',
    noregxp: [
        '([\\W]|^)(bnb[0-9a-z]{2})([\\W]|$)',
        '([\\W]|^)((cosmos1|terra1|tcro1|tcrocncl1)[a-zA-HJ-NP-Z0-9]{38,70})([\\W]|$)',
    ],
    graphqlV2: true,
    query: addressInfoQuery(network.v2Network),
    toAddressInfo: (response: EvmAddressInfoV2Response) => {
        const { EVM } = response
        const balance = EVM.balance?.[0]?.Balance
        return {
            [network.network]: {
                address: {
                    balance: Number(balance?.Amount ?? 0),
                    balance_usd: network.nativeUsdPeg ?
                        Number(balance?.Amount ?? 0) * network.nativeUsdPeg :
                        Number(balance?.AmountInUSD ?? 0),
                },
                ins: toCounts(EVM.ins),
                currencyIn: toCurrencies(EVM.currencyIn, network),
                outs: toCounts(EVM.outs),
                currencyOut: toCurrencies(EVM.currencyOut, network),
            },
        }
    },
})

const blockchainOf = (name: string) => createEvmV2Blockchain(getEvmV2Network(name)!)

export const ethereum = blockchainOf("ethereum")
export const bsc = blockchainOf("bsc")
export const matic = blockchainOf("matic")
export const arbitrum = blockchainOf("arbitrum")
export const optimism = blockchainOf("optimism")
export const base = blockchainOf("base")
export const robinhood = blockchainOf("robinhood")
export const arc = blockchainOf("arc")
